import { Injectable, signal } from '@angular/core';

export type NotifyKind = 'success' | 'info' | 'warning' | 'error';

export interface NotifyAction {
  label: string;
  run: () => void;
}

export interface NotifyOptions {
  /** The second, quieter part of the line: "· 4.39 ha". */
  detail?: string;
  action?: NotifyAction;
  /** Stays until closed. Errors always do. */
  sticky?: boolean;
  /** How long a non-sticky notice stays, in ms. */
  durationMs?: number;
}

export interface Notice {
  id: number;
  kind: NotifyKind;
  title: string;
  detail?: string;
  action?: NotifyAction;
  sticky: boolean;
}

/** Where a page wants its notices: centred across this strip of the window, in px from its top left. */
export interface NotifyRegion {
  left: number;
  width: number;
  top: number;
}

const DURATION_MS: Record<NotifyKind, number> = { success: 5000, info: 6000, warning: 8000, error: 0 };
/** More than this and the oldest goes: a stack that fills the corner hides the map. */
export const MAX_NOTICES = 4;

/**
 * App notifications as a stack at the top right, newest on top (design canvas "App notifications",
 * option B, chosen 12/10/2026). Success and info close by themselves, warnings stay a little longer,
 * errors stay until closed. Hovering the stack holds every timer, so a notice is not gone while it is
 * being read. Rendered once by NotifyStackComponent at the app root.
 *
 * A page can ask for its notices elsewhere with setRegion: /maps centres them at the top of the map
 * beside its panel (Heinrich, 12/10/2026). With no region they sit at the top right.
 *
 * Field boundary management uses it first; the other 450-odd MatSnackBar calls move over later.
 */
@Injectable({ providedIn: 'root' })
export class NotifyService {
  readonly notices = signal<Notice[]>([]);
  readonly region = signal<NotifyRegion | null>(null);

  /** A page's strip for notices, or null to put them back at the top right (the page is left). */
  setRegion(region: NotifyRegion | null): void {
    this.region.set(region);
  }

  private nextId = 1;
  private timers = new Map<number, { handle: ReturnType<typeof setTimeout> | null; endsAt: number; remaining: number }>();
  private paused = false;

  success(title: string, options: NotifyOptions = {}): number { return this.show('success', title, options); }
  info(title: string, options: NotifyOptions = {}): number { return this.show('info', title, options); }
  warning(title: string, options: NotifyOptions = {}): number { return this.show('warning', title, options); }
  error(title: string, options: NotifyOptions = {}): number { return this.show('error', title, options); }

  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer?.handle) { clearTimeout(timer.handle); }
    this.timers.delete(id);
    this.notices.update(list => list.filter(n => n.id !== id));
  }

  /** The notice's action, then the notice closes. */
  act(id: number): void {
    const notice = this.notices().find(n => n.id === id);
    if (!notice?.action) { return; }
    this.dismiss(id);
    notice.action.run();
  }

  /** While the pointer is over the stack nothing closes by itself. */
  pause(): void {
    if (this.paused) { return; }
    this.paused = true;
    const now = Date.now();
    this.timers.forEach(t => {
      if (t.handle) { clearTimeout(t.handle); t.handle = null; }
      t.remaining = Math.max(0, t.endsAt - now);
    });
  }

  resume(): void {
    if (!this.paused) { return; }
    this.paused = false;
    this.timers.forEach((t, id) => this.arm(id, t.remaining));
  }

  private show(kind: NotifyKind, title: string, options: NotifyOptions): number {
    const id = this.nextId++;
    const sticky = kind === 'error' || !!options.sticky;
    const notice: Notice = { id, kind, title, detail: options.detail, action: options.action, sticky };
    const list = [notice, ...this.notices()];
    list.slice(MAX_NOTICES).forEach(old => this.dismiss(old.id));
    this.notices.set(list.slice(0, MAX_NOTICES));
    if (!sticky) {
      const ms = options.durationMs ?? DURATION_MS[kind];
      this.timers.set(id, { handle: null, endsAt: Date.now() + ms, remaining: ms });
      if (!this.paused) { this.arm(id, ms); }
    }
    return id;
  }

  private arm(id: number, ms: number): void {
    const timer = this.timers.get(id);
    if (!timer) { return; }
    timer.endsAt = Date.now() + ms;
    timer.handle = setTimeout(() => this.dismiss(id), ms);
  }
}
